/**
 * tools-lib.ts — Hàm thuần cho thẻ "Công cụ" của màn Dạy AI: suy ra danh sách công cụ ĐÃ GẮN từ toolsJson +
 * settings (không tạo cơ chế song song), và các thao tác gắn / gỡ ánh xạ về cùng dữ liệu:
 *   - Công cụ Evotech: sản phẩm = nguồn `evotech` trong dataSources.products; tồn kho = inStockOnly (khi có nguồn
 *     sản phẩm evotech); đơn hàng = nguồn `evotech` trong dataSources.orders; thành viên = memberLookup.
 *   - Công cụ eCDP: tìm kiến thức (search_knowledge), sản phẩm nội bộ (nguồn import/manual), đơn qua API (nguồn api),
 *     đặt lịch (settings.appointmentEnabled), chuyển người (settings.handoff.enabled), dừng trả lời (settings.autoStop).
 */
import type { AiBotToolsJson, AiDataSource } from '@/hooks/use-ai'
import type { BotSettings } from './bot-settings'
import { currentSelection, toggleSource, setInStockOnly } from '../data-sources-lib'
import { i18n } from '@/lib/i18n'

export type ToolStatus = 'active' | 'unauthorized' | 'disconnected' | 'off'
export type AttachedTool = {
  id: string
  name: string
  description: string
  /** 'ecdp' | 'evotech' | khoá nguồn khác */
  platform: string
  status: ToolStatus
  enabled: boolean
  /** Bật/tắt đổi được bằng công tắc (false = chỉ điều hướng, vd Chuyển người). */
  toggleable: boolean
  /** Có nút gỡ. */
  removable: boolean
}

const LOCAL_PRODUCT_KEYS = ['import', 'manual']

function platformStatus(key: string, sources: AiDataSource[]): ToolStatus {
  const s = sources.find((x) => x.key === key)
  if (!s) return 'disconnected'
  return s.status === 'unauthorized' ? 'unauthorized' : 'active'
}

export function attachedTools(tools: AiBotToolsJson | null | undefined, settings: BotSettings, sources: AiDataSource[]): AttachedTool[] {
  const sel = currentSelection(tools, sources)
  const out: AttachedTool[] = []
  const kbOn = tools?.search_knowledge?.enabled ?? true
  out.push({ id: 'ecdp.knowledge', name: i18n.t('aiTrainLib:toolsLib.knowledge.name'), description: i18n.t('aiTrainLib:toolsLib.knowledge.description'), platform: 'ecdp', status: kbOn ? 'active' : 'off', enabled: kbOn, toggleable: true, removable: false })

  const spOn = tools?.search_products?.enabled ?? true
  const localProducts = sel.products.filter((k) => LOCAL_PRODUCT_KEYS.includes(k))
  if (localProducts.length) {
    out.push({ id: 'ecdp.products', name: i18n.t('aiTrainLib:toolsLib.products.name'), description: i18n.t('aiTrainLib:toolsLib.products.description', { count: localProducts.length }), platform: 'ecdp', status: spOn ? 'active' : 'off', enabled: spOn, toggleable: true, removable: true })
  }
  for (const key of sel.products.filter((k) => !LOCAL_PRODUCT_KEYS.includes(k))) {
    const st = platformStatus(key, sources)
    out.push({ id: `${key}.products`, name: key === 'evotech' ? i18n.t('aiTrainLib:toolsLib.synced.nameEvotech') : i18n.t('aiTrainLib:toolsLib.synced.name', { key }), description: i18n.t('aiTrainLib:toolsLib.synced.description'), platform: key, status: spOn ? st : 'off', enabled: spOn, toggleable: true, removable: true })
    if (key === 'evotech' && tools?.inStockOnly) {
      out.push({ id: 'evotech.stock', name: i18n.t('aiTrainLib:toolsLib.stock.name'), description: i18n.t('aiTrainLib:toolsLib.stock.description'), platform: key, status: st, enabled: true, toggleable: true, removable: true })
    }
  }
  for (const key of sel.orders) {
    const local = key === 'api'
    out.push({ id: `${key}.orders`, name: local ? i18n.t('aiTrainLib:toolsLib.orders.nameApi') : key === 'evotech' ? i18n.t('aiTrainLib:toolsLib.orders.nameEvotech') : i18n.t('aiTrainLib:toolsLib.orders.name', { key }), description: i18n.t('aiTrainLib:toolsLib.orders.description'), platform: local ? 'ecdp' : key, status: local ? 'active' : platformStatus(key, sources), enabled: true, toggleable: true, removable: true })
  }
  if (tools?.memberLookup) {
    const integ = sources.find((x) => x.key === 'evotech')
    out.push({ id: 'evotech.member', name: i18n.t('aiTrainLib:toolsLib.member.name'), description: i18n.t('aiTrainLib:toolsLib.member.description'), platform: 'evotech', status: integ ? (integ.status === 'unauthorized' ? 'unauthorized' : 'active') : 'active', enabled: true, toggleable: true, removable: true })
  }
  if (tools?.orderCreate) {
    const integ = sources.find((x) => x.key === 'evotech')
    out.push({ id: 'evotech.order_create', name: i18n.t('aiTrainLib:toolsLib.orderCreate.name'), description: i18n.t('aiTrainLib:toolsLib.orderCreate.description'), platform: 'evotech', status: integ ? (integ.status === 'unauthorized' ? 'unauthorized' : 'active') : 'active', enabled: true, toggleable: true, removable: true })
  }
  out.push({ id: 'ecdp.appointment', name: i18n.t('aiTrainLib:toolsLib.appointment.name'), description: i18n.t('aiTrainLib:toolsLib.appointment.description'), platform: 'ecdp', status: settings.appointmentEnabled ? 'active' : 'off', enabled: settings.appointmentEnabled, toggleable: true, removable: false })
  out.push({ id: 'ecdp.handoff', name: i18n.t('aiTrainLib:toolsLib.handoff.name'), description: i18n.t('aiTrainLib:toolsLib.handoff.description'), platform: 'ecdp', status: settings.handoff.enabled ? 'active' : 'off', enabled: settings.handoff.enabled, toggleable: false, removable: false })
  out.push({ id: 'ecdp.autostop', name: i18n.t('aiTrainLib:toolsLib.autostop.name'), description: i18n.t('aiTrainLib:toolsLib.autostop.description'), platform: 'ecdp', status: settings.autoStop.enabled ? 'active' : 'off', enabled: settings.autoStop.enabled, toggleable: false, removable: false })
  return out
}

export const TOOL_STATUS_LABEL: Record<ToolStatus, string> = {
  get active() { return i18n.t('aiTrainLib:toolsLib.status.active') },
  get unauthorized() { return i18n.t('aiTrainLib:toolsLib.status.unauthorized') },
  get disconnected() { return i18n.t('aiTrainLib:toolsLib.status.disconnected') },
  get off() { return i18n.t('aiTrainLib:toolsLib.status.off') },
}

/** Đã gắn công cụ nền tảng `<platform>.<kind>` chưa. */
export function platformToolAttached(id: string, tools: AiBotToolsJson | null | undefined, sources: AiDataSource[]): boolean {
  const [platform, kind] = id.split('.')
  const sel = currentSelection(tools, sources)
  if (kind === 'products') return sel.products.includes(platform) && (tools?.search_products?.enabled ?? true)
  if (kind === 'stock') return sel.products.includes(platform) && tools?.inStockOnly === true
  if (kind === 'orders') return sel.orders.includes(platform)
  if (kind === 'member') return tools?.memberLookup === true
  if (kind === 'order_create') return tools?.orderCreate === true
  return false
}

/** Sửa cấu hình riêng của "Tạo đơn nháp cho khách" (trần giá trị, phương thức AI được dùng, hạn nháp phút). */
export function setOrderConfig(
  tools: AiBotToolsJson | null | undefined,
  patch: { orderMaxValue?: number; orderPayTypes?: ('COD' | 'Transfer')[]; orderDraftTtlMinutes?: number },
): AiBotToolsJson {
  return { ...(tools ?? {}), ...patch }
}

/** Gắn / gỡ công cụ nền tảng → toolsJson mới. */
export function setPlatformTool(id: string, on: boolean, tools: AiBotToolsJson | null | undefined, sources: AiDataSource[]): AiBotToolsJson {
  const [platform, kind] = id.split('.')
  if (kind === 'products') {
    const next = toggleSource(tools, sources, 'products', platform, on)
    if (on) next.search_products = { ...(next.search_products ?? { enabled: true, guardrail: { categoryIds: [] } }), enabled: true }
    return next
  }
  if (kind === 'stock') {
    let next: AiBotToolsJson = { ...(tools ?? {}) }
    if (on && !currentSelection(tools, sources).products.includes(platform)) next = toggleSource(next, sources, 'products', platform, true)
    return setInStockOnly(next, on)
  }
  if (kind === 'orders') return toggleSource(tools, sources, 'orders', platform, on)
  if (kind === 'member') return { ...(tools ?? {}), memberLookup: on }
  if (kind === 'order_create') return { ...(tools ?? {}), orderCreate: on }
  return { ...(tools ?? {}) }
}
